/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-props-no-spreading */
import { FunctionComponent, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { convertSizeToNumber } from '@/_shared/helpers/convertSizeToNumber';
import { getModeTheme } from '@/_shared/styles/state/selectors';
import { Tooltip } from '../Tooltip';
import { TooltipProps } from '../Tooltip/types';
import { buildCheckboxColumn } from './helpers/checkboxColumn';
import { SortArrow, SortableHeaderBox } from './TableSortArrow';
import { Column, HoverStyledBox, SortDirection, StyledScrollBox, TableProps } from './types';
import { useTable } from './useTable';

export const Table: FunctionComponent<TableProps> = ({
  columns,
  headerProps = {},
  columnWrapperProps = {},
  columnTitleProps = {},
  rowWrapperProps = {},
  cellContentProps = {},
  rowHoverStyle = {},
  defaultCellVariant = 'p2/light',
  columnGap = 'xs',
  onRowClick,
  onHoverRow,
  rowData,
  headerTestId,
  rowTestId,
  selectedRow,
  hasHover = false,
  orderBy,
  onSort,
  showCheckboxes = false,
  selectedItems = [],
  onSelectionChange,
  getRowId,
  bulkActionLabel,
  bulkActionOnClick,
  ...props
}) => {
  const t = useTranslations('table');
  const mode = useSelector(getModeTheme());
  const colGap = useThemeValue('space', columnGap);
  const tooltipGap = useThemeValue('space', 'xxxs');
  const gapXS = convertSizeToNumber(`${useThemeValue('space', 'xs')}`);
  const [hoveredId, setHoveredId] = useState<string | undefined>(undefined);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getId = getRowId ?? ((item: any) => item.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allPageIds = showCheckboxes ? (rowData || []).map((item: any) => getId(item)) : [];
  const allPageSelected =
    showCheckboxes && allPageIds.length > 0 && allPageIds.every(id => selectedItems.includes(id));

  const checkboxColumn: Column | null = showCheckboxes
    ? buildCheckboxColumn({
        allPageSelected,
        allPageIds,
        selectedItems,
        hoveredId,
        onSelectionChange,
        getId,
      })
    : null;

  const resolvedColumns = showCheckboxes && checkboxColumn ? [checkboxColumn, ...columns] : columns;

  const { gridTemplateColumns, renderRowCell, rowProps, mergeStyle } = useTable({
    columns: resolvedColumns,
    defaultCellVariant,
    rowWrapperProps,
  });

  const rowStyleSelected =
    mode === 'dark'
      ? { backgroundColor: 'grey/dark/background', borderRadius: '10px' }
      : { backgroundColor: 'grey/dark/background', borderRadius: '10px' };

  const handleSortClick = useCallback(
    (column: Column) => {
      if (!onSort) {
        return;
      }
      const sortField = column.sortKey || column.key;
      const currentDirection = orderBy?.[sortField];
      const nextDirection: `${SortDirection}` =
        currentDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
      onSort({ [sortField]: nextDirection });
    },
    [onSort, orderBy],
  );

  const isClickInsideRowRef = useRef(false);

  if (!resolvedColumns || resolvedColumns.length === 0) {
    return null;
  }

  return (
    <>
      {showCheckboxes && selectedItems.length > 0 && (
        <Card
          backgroundColor='primary/background'
          display='flex'
          alignItems='center'
          mb='s'
          p='xs'
          justifyContent='space-between'
          style={{ gap: gapXS }}
        >
          <Box display='flex' style={{ gap: gapXS }} alignItems='center'>
            <Typography variant='p2/light'>
              {t('selectedCount', { count: selectedItems.length })}
            </Typography>
            <Button
              variant='primary'
              px='xs'
              height='m'
              onClick={() => bulkActionOnClick?.(selectedItems)}
            >
              {bulkActionLabel}
            </Button>
          </Box>
          <Box>
            <Typography
              as='span'
              variant='p2/light'
              color='primary'
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => onSelectionChange?.([])}
            >
              {t('deselectAll')}
            </Typography>
          </Box>
        </Card>
      )}
      <StyledScrollBox
        display='grid'
        overflowX='auto'
        {...mergeStyle(props, { gridTemplateColumns, gridColumnGap: colGap })}
      >
        <Box
          display='grid'
          gridColumn={`span ${resolvedColumns.length}`}
          data-testid={headerTestId}
          mb='xs'
          {...mergeStyle(headerProps, { gridTemplateColumns: 'subgrid' })}
        >
          {resolvedColumns.map(c => {
            const isSortable = !!c.sortable;
            const sortField = c.sortKey || c.key;
            const sortDirection = orderBy?.[sortField];
            const isSorted = !!sortDirection;
            return (
              <SortableHeaderBox
                key={c.key}
                {...columnWrapperProps}
                {...c.columnProps}
                onClick={isSortable ? () => handleSortClick(c) : undefined}
                style={isSortable ? { cursor: 'pointer' } : undefined}
              >
                {typeof c.title === 'string' ? (
                  <Typography
                    variant='p2/light'
                    fontSize='xs'
                    color='grey/light'
                    pl='xs'
                    display='flex'
                    alignItems='center'
                    style={{ gap: tooltipGap }}
                    {...(typeof columnTitleProps === 'function'
                      ? columnTitleProps(c.key)
                      : columnTitleProps)}
                  >
                    {c.title}
                    {c.titleTooltip && (
                      <Tooltip
                        iconProps={{ size: 'xs', fill: 'grey/light' }}
                        {...(c.titleTooltipProps as TooltipProps)}
                      >
                        {c.titleTooltip}
                      </Tooltip>
                    )}
                    {isSortable && <SortArrow direction={sortDirection} isSorted={isSorted} />}
                  </Typography>
                ) : (
                  c.title
                )}
              </SortableHeaderBox>
            );
          })}
        </Box>
        {/* HEADER SEPARATOR */}
        <Box display='grid' gridColumn={`span ${resolvedColumns.length}`} mb='xs' px='xs'>
          <Box backgroundColor='grey/light' width='100%' height={1} />
        </Box>
        {rowData?.map((item, index) => (
          <HoverStyledBox
            key={`row-${index}`}
            py='xs'
            display='grid'
            gridColumn={`span ${resolvedColumns.length}`}
            data-testid={`${rowTestId}-${index}`}
            {...mergeStyle(rowProps(item, index), { gridTemplateColumns: 'subgrid' })}
            {...(index === selectedRow ? { ...rowStyleSelected } : {})}
            rowHoverStyle={
              mode === 'dark'
                ? { backgroundColor: 'rgb(50, 56, 64)', ...rowHoverStyle }
                : { backgroundColor: '#FCFCFF', ...rowHoverStyle }
            }
            onClick={() => onRowClick?.(item, index)}
            onMouseDown={() => {
              if (hasHover) isClickInsideRowRef.current = true;
            }}
            onMouseEnter={() => {
              isClickInsideRowRef.current = false;
              if (hasHover) onHoverRow?.(item, index);
              if (showCheckboxes) setHoveredId(getId(item));
            }}
            onMouseLeave={() => {
              if (hasHover && !isClickInsideRowRef.current) onHoverRow?.(undefined, index);
              if (showCheckboxes) setHoveredId(undefined);
            }}
          >
            {resolvedColumns.map((c, colIndex) => (
              <Box
                pl='xs'
                pr={colIndex === resolvedColumns.length - 1 ? 'xs' : undefined}
                key={`row-${index}-col-${c.key}`}
                {...(typeof cellContentProps === 'function'
                  ? cellContentProps(c.key, item, index)
                  : cellContentProps)}
              >
                {renderRowCell(item, c.renderValue)}
              </Box>
            ))}
          </HoverStyledBox>
        ))}
      </StyledScrollBox>
    </>
  );
};
